import styled, { css } from 'styled-components/macro'
import { themeGet } from '@styled-system/theme-get'
import {
  ShowPageContainer,
  ShowPageItem,
  ShowPageItemBody,
} from 'src/components/PageLayout/ShowPage'
import palette from 'src/designSystem/variables/palette'
import { ReactComponent as SuccessBadgeIcon } from 'src/components/Icons/Basic/icons_badge_success.svg'
import { ModeCommentSVG } from 'src/components/Icons/Basic'
import { ShowPageAccordionIcon } from 'src/components/Reusable/Accordions/ShowPage/ShowPageAccordions.styles'

// The link blue the design uses, which has no palette token -- palette.blue[700]
// is #0095A9, a teal, and reads as green next to it. Already a loose literal in
// ~8 places including TextAccordion, which draws the "Why this answer" toggle
// this panel belongs to; named once here so the questionnaire surfaces at least
// agree with each other.
export const LINK_BLUE = '#2196f3'

// The sidebar's tab strip and this row are the first thing in their respective
// columns, so the bordered panels below them line up only if the two occupy the
// same vertical space. That footprint is the Tabs component's .nav-tabs (40px
// tall) plus its own 12px bottom margin -- both mirrored here, since CSS cannot
// read them off the sibling column.
export const QUESTIONNAIRE_HEADER_STRIP_HEIGHT = '40px'
export const QUESTIONNAIRE_HEADER_STRIP_GAP = '12px'

export const QuestionnaireHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: ${QUESTIONNAIRE_HEADER_STRIP_HEIGHT};
  margin-bottom: ${QUESTIONNAIRE_HEADER_STRIP_GAP};
`

export const QuestionnairePanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${palette.neutral[900]};
  margin: 0;
  white-space: nowrap;
`

export const ProgressBarTrack = styled.div`
  flex: 1;
  height: 16px;
  border-radius: 20px;
  background: ${palette.neutral[50]};
  overflow: hidden;
`

export const ProgressBarFill = styled.div<{ $percent: number }>`
  height: 100%;
  border-radius: 20px;
  background: linear-gradient(
    90deg,
    #6616ee -23.97%,
    #5fd3ec 50.41%,
    #c0bee7 100%
  );
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.12);
  width: ${({ $percent }) => `${$percent}%`};
  transition: width 0.2s ease-in-out;
`

export const ProgressBarLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${palette.neutral[900]};
  white-space: nowrap;
`

export const SectionHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
`

export const SectionHeaderTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${palette.neutral[900]};
  margin: 0;
`

export const SectionHeaderDescription = styled.div`
  margin: -4px 0 12px;
  color: ${palette.neutral[700]};
`

export const ExpandCollapseRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
`

export const ExpandCollapseLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${palette.blue[600]};
  cursor: pointer;
  text-transform: uppercase;

  &:disabled {
    color: ${palette.neutral[300]};
    cursor: default;
  }
`

export const ExpandCollapseDivider = styled.span`
  color: ${palette.neutral[200]};
`

// The scroll container for the section index.
//
// It has to bound itself: the tab pane above it is already sized to the
// workspace (a flex column with min-height 0), but this list was an unstyled
// div that simply grew to its content -- a 55-question section runs to several
// thousand pixels. Nothing between here and ShowPageContainer scrolls, so the
// overflow fell through to that shared scroller: the whole page scrolled, and
// the index ran past the bottom of the questions column beside it instead of
// ending level with it.
//
// min-height: 0 is the load-bearing half. A flex item's default min-height is
// auto, which refuses to shrink below its content, so flex: 1 1 auto alone
// would leave it full height and overflow-y with nothing to do.
export const SectionListScroll = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${themeGet('space.xs')}px;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
`

// The section index's cards, without a gap: a drop line between two of them
// must not open one.
export const SectionListRows = styled.div`
  display: flex;
  flex-direction: column;
`

// A section card and, where the form can be edited, its grip. The grip stays
// level with the section's name however far the card opens.
export const SectionListRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${themeGet('space.xxs')}px;
  margin-bottom: ${themeGet('space.xs')}px;

  > span[role='button'] {
    margin-top: 10px;
  }
`

// One section in the index. Darkened while it is the section the questions
// column shows; $dropTarget while a dragged question is over it.
export const SectionListItem = styled.div<{
  $isSelected: boolean
  $dropTarget?: boolean
}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  min-width: 0;
  padding: 12px;
  border: 1px solid
    ${({ $dropTarget }) =>
      $dropTarget ? palette.blue[500] : palette.neutral[100]};
  border-radius: 12px;
  text-align: left;
  background: ${({ $isSelected }) =>
    $isSelected ? palette.neutral[400] : palette.neutral.white};
  color: ${({ $isSelected }) =>
    $isSelected ? palette.neutral.white : palette.neutral[900]};
`

// The name and what is known about the section, beside the card's actions and
// its chevron.
export const SectionListItemTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`

// Selects the section. The questions below it are their own buttons, so the
// card cannot be one -- a button inside a button is unreachable by keyboard.
export const SectionListItemHeader = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
  padding: 0;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  color: inherit;
`

export const SectionListItemName = styled.span`
  font-size: 16px;
  font-weight: 500;
  line-height: 1.25;
  overflow-wrap: anywhere;
`

// Opens and closes the question list without selecting the section.
export const SectionListItemToggle = styled.button`
  display: inline-flex;
  flex: 0 0 auto;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
`

// The app's accordion chevron, which brings its own open/closed flip, recoloured
// to follow the card. The shared one is pinned to neutral[900], which all but
// disappears once an open section turns dark -- overridden here rather than
// changed there, since every other accordion in the app sits on white.
export const SectionListItemChevron = styled(ShowPageAccordionIcon)`
  flex: 0 0 auto;

  // The shared icon pins color to neutral[900], so currentColor below would
  // resolve to that dark value rather than to the card's. Inheriting first is
  // what lets the card decide.
  color: inherit;

  // The glyph paints through its path's fill, which defaults to black and does
  // not follow the color property on its own. Pointing the fill at currentColor
  // is what turns the chevron white with the rest of the card when it opens.
  //
  // Only the glyph: the icon also carries a 24x24 spacer path with fill="none",
  // and filling that one paints a solid square over the whole chevron.
  path:not([fill]) {
    fill: currentColor;
  }
`

export const SectionListItemMeta = styled.span`
  font-size: 12px;
  letter-spacing: 0.5px;
  color: currentColor;
`

// Opens and closes by animating a grid row from 0fr to 1fr, which needs no
// measured height and no max-height guess -- a max-height large enough for a
// forty-question section makes a short one snap open instantly, and one sized
// for a short section clips a long one.
//
// The row is the animated thing, so the inner wrapper carries overflow: hidden
// and the questions keep their own layout inside it.
export const SectionListQuestions = styled.div<{ $isOpen: boolean }>`
  display: grid;
  width: 100%;
  grid-template-rows: ${({ $isOpen }) => ($isOpen ? '1fr' : '0fr')};
  transition: grid-template-rows 0.25s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

export const SectionListQuestionsInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  overflow: hidden;
`

// One question inside an open section. The number and the text sit in one
// paragraph rather than on two lines: they are read as a single sentence, and
// the number is what the reader is scanning for.
export const SectionListQuestionButton = styled.button`
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  color: inherit;
  font-size: 14px;
  line-height: 1.25;

  &:hover {
    text-decoration: underline;
  }
`

export const SectionListQuestionNumber = styled.span`
  font-size: 16px;
  font-weight: 500;
`

// Takes the header's free space, so whatever follows -- a comment count, the
// actions, the chevron -- sits at the right edge.
export const QuestionHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 4px;
  text-align: left;
`

export const QuestionNumber = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${palette.neutral[900]};
`

export const QuestionNumberRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

// The outcome pill sits on its OWN row under the question number, not beside
// it -- per the design, and because it shares that row with the confidence
// pill once that lands.
export const QuestionBadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`

export const SuccessBadge = styled(SuccessBadgeIcon)`
  width: 16px;
  height: 16px;
`

export const QuestionText = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${palette.neutral[700]};
  white-space: normal;
`

export const CommentBadgeContainer = styled.span<{ $hasComments: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${palette.neutral[600]};
  cursor: pointer;
  margin-left: auto;
  margin-right: 16px;
  flex-shrink: 0;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.2s ease;
  user-select: none;

  // Hidden until the question is hovered, per the design -- an empty comment
  // icon on every row is noise. A question that HAS comments keeps its badge
  // visible: hiding a count behind hover would hide information, not clutter.
  // Kept focusable-visible too, so the keyboard path is not hover-only.
  opacity: ${({ $hasComments }) => ($hasComments ? 1 : 0)};
  transition: opacity 0.15s ease, color 0.2s ease, background-color 0.2s ease;

  .accordion-button:hover &,
  &:focus-visible,
  &:focus-within {
    opacity: 1;
  }

  &:hover {
    color: ${palette.neutral[900]};
    background-color: ${palette.neutral[50]};
  }
`

// Count before the icon, per the design. Poppins Medium 14px in Neutral/600.
export const CommentBadgeCount = styled.span`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.25;
  color: inherit;
`

export const CommentBadgeIconWrap = styled.span`
  position: relative;
  display: inline-flex;
  width: 16px;
  height: 16px;
`

export const CommentBadgeModeCommentIcon = styled(ModeCommentSVG)`
  width: 16px;
  height: 16px;
  path {
    fill: currentColor;
  }
`

// 8px Magenta/600 dot overlapping the bubble's top-right corner.
export const CommentUnreadDot = styled.span`
  position: absolute;
  top: -2px;
  right: -3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${palette.magenta[600]};
`

export const AnswerInputContainer = styled.div`
  padding-bottom: 8px;
`

export const AnswerTextarea = styled.textarea`
  width: 100%;
  min-height: 96px;
  padding: 12px 16px;
  border: 1px solid ${palette.neutral[200]};
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  color: ${palette.neutral[900]};
  resize: vertical;

  &::placeholder {
    color: ${palette.neutral[400]};
  }

  &:disabled {
    background: ${palette.neutral[30]};
    color: ${palette.neutral[500]};
  }

  &:focus {
    outline: none;
    border-color: ${palette.neutral[400]};
  }
`

export const AnswerOptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const AnswerOptionLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: ${palette.neutral[900]};
  cursor: pointer;
`

export const AnswerOptionInput = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`

// Says why the answers cannot be edited, above the inputs it explains.
export const ReadOnlyNotice = styled.p`
  color: ${palette.neutral[500]};
  font-size: 14px;
  font-style: italic;
  margin: 0 0 12px;
`

// Comments panel styles
// The frame every sidebar tab's content sits in: a full-height rounded panel
// matching the questions panel across from it. Shared so the tabs cannot end up
// with different borders, which is what left Attachments looking shorter than
// Comments when it was in fact the same height with nothing drawn around it.
export const QuestionnaireSidebarPanel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${palette.neutral.white};
  border: 1px solid ${palette.neutral[100]};
  border-radius: 12px;
`

// The question a thread hangs off, above its comments. The gap below is what
// separates the question from the conversation about it -- without it the first
// comment reads as part of the question.
export const CommentsHeaderSubtitle = styled.div`
  font-size: 13px;
  color: ${palette.neutral[600]};
  margin-top: 4px;
  margin-bottom: 12px;
`

export const CommentsListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const CommentItemRow = styled.div`
  display: flex;
  gap: 6px;
  position: relative;

  &:hover {
    .delete-btn {
      opacity: 1;
      visibility: visible;
    }
  }
`

// Per the design: the reader's own comments carry the dark avatar, everyone
// else's the grey one, so a thread is scannable for "what did I say" without
// reading a single name.
export const CommentAvatar = styled.div<{ $isSelf?: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ $isSelf }) =>
    $isSelf ? palette.neutral[900] : palette.neutral[400]};
  color: ${palette.neutral.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.5px;
  flex-shrink: 0;
`

export const CommentContent = styled.div`
  flex: 1;
  min-width: 0;
`

// "Lucy Smith * 2026-03-21, 9 AM" reads as one quiet line above the comment.
// The name used to be the loudest thing in the row, which put the emphasis on
// who spoke rather than on what they said.
// Author and timestamp, on one line. Smaller than the comment body on purpose:
// this is attribution, not content, and the rail is a 360px column -- an email
// address plus a date at body size wrapped onto a second line and gave every
// comment a three-line header.
export const CommentContentHeader = styled.div`
  display: flex;
  gap: 4px;
  align-items: baseline;
  /* No wrapping: the NAME gives way (it ellipsizes below), because a truncated
     address still identifies who spoke, while a truncated date says nothing. */
  flex-wrap: nowrap;
  margin-bottom: 2px;
  font-size: 12px;
  line-height: 1.5;
  color: ${palette.neutral[400]};
`

export const CommentAuthorName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* Shrinkable rather than fixed-width: min-width 0 is what actually lets a
     flex child ellipsize instead of forcing its container wider. */
  flex: 0 1 auto;
  min-width: 0;
`

export const CommentTimestamp = styled.span`
  white-space: nowrap;
`

export const CommentBody = styled.div`
  font-size: 14px;
  color: ${palette.neutral[900]};
  line-height: 1.5;
  overflow-wrap: anywhere;

  p {
    margin: 0;
  }
`

export const CommentDeleteButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  background: none;
  border: none;
  color: ${palette.neutral[300]};
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
  opacity: 0;
  visibility: hidden;

  &:hover {
    color: ${palette.magenta[500]};
    background-color: ${palette.magenta[30]};
  }
`

// No rule above the composer: the design separates it by space, and the
// textarea's own border already closes the panel off.
export const CommentEditorContainer = styled.div`
  /* No inset of its own: the scroll area around it already provides one, and
     doubling them left the box visibly narrower than the comments above it. */
  padding: 12px 0 0;
  /* Transparent, not white: the selected thread behind this is tinted, and a
     hardcoded white here punched a white block out of the middle of it. The
     textarea supplies its own background, which is the only part that should
     read as a field. */
  background: transparent;

  textarea {
    resize: none;
    border-radius: 12px;
    border-color: ${palette.neutral[100]};
    color: ${palette.neutral[900]};
    font-size: 14px;

    &::placeholder {
      color: ${palette.neutral[400]};
    }
  }
`

// One empty state for every questionnaire side panel. Comments and
// Attachments each had their own before -- a bordered, centred block versus a
// bare left-aligned paragraph -- so the two tabs looked unrelated when empty.
export const QuestionnaireEmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 32px;
  text-align: center;
  color: ${palette.neutral[400]};
`

export const QuestionnaireEmptyStateTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
  color: ${palette.neutral[500]};
`

export const QuestionnaireEmptyStateText = styled.div`
  font-size: 12px;
`

// Flat view / preview modal styles
export const PreviewModalSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  // A rule between sections rather than under each title: the design separates
  // the groups, and a border on the title itself read as an underline.
  & + & {
    border-top: 1px solid ${palette.neutral[100]};
    padding-top: 24px;
  }
`

export const PreviewModalSectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  line-height: 1.25;
  color: ${palette.neutral[900]};
  margin: 0;
`

export const PreviewModalQuestionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

// No card, no padding, no fill: the preview is a document to read and print,
// not a form. The question and its answer are two lines of text.
export const PreviewModalQuestionItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

// One paragraph, so the question text runs on from its number rather than
// sitting under it.
export const PreviewModalQuestionText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.25;
  color: ${palette.neutral[600]};
`

export const PreviewModalQuestionNumber = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: ${palette.neutral[900]};
  margin-right: 4px;
`

export const PreviewModalAnswerText = styled.div`
  font-size: 14px;
  line-height: 1.25;
  color: ${palette.neutral[900]};
  // Free text answers can hold newlines (Shift+Enter in the answer box), and
  // this view is what gets printed -- so keep them, and keep a long unbroken
  // answer from running out of the page.
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`

// Wraps a free-text answer and its confirm/cancel controls, which only appear
// once the draft differs from the saved answer.
export const FreeTextAnswerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

// Shared by every questionnaire version preview (the vendor portal's, the
// customer's received questionnaires, and the customer's own sent ones), which
// is why these live here rather than beside any one of them.
export const PreviewModalTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-right: 40px;
`

export const PreviewModalSubtitle = styled.p`
  font-size: 14px;
  color: ${palette.neutral[600]};
  margin: 0 0 16px 0;
`

export const PreviewModalSubtitleCount = styled.strong`
  font-weight: 700;
  color: ${palette.neutral[900]};
`

// ─── Supplemental inputs (AP-19838) ───────────────────────────────────────

export const SupplementalContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`

export const SupplementalLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${palette.neutral[600]};
`

export const AttachDocumentButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 13px;
  color: ${LINK_BLUE};
  align-self: flex-start;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

export const AttachmentChipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

// An attached file matches the playbook modal's attached-input row
// (DocumentDisplayContainer, playbooks.styles): mime-type icon, name, remove.
// No tile around it -- the attachment belongs to the answer above it, and a
// bordered card reads as a separate object.
export const AttachmentChip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
`

// The chip's name rendered where it cannot be opened: same type, none of the
// affordances that promise a click.
export const AttachmentChipNameStatic = styled.span`
  font-size: 13px;
  color: ${palette.neutral[900]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: none;
  border: none;
  padding: 0;
  text-align: left;
`

export const AttachmentChipName = styled.button`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 14px;
  color: ${palette.neutral[900]};
  text-decoration: underline;
`

export const AttachmentChipRemove = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${palette.neutral[400]};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

// ─── Attachments panel (AP-19838) ─────────────────────────────────────────

export const AttachmentsPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  /* The scrolling part inside the card above, which is what carries the frame
     and the height. */
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`

// The section heading is the accordion's outer control, so it is a button
// rather than a bare heading. Typography follows the comments rail's section
// rows -- both are "the collapsible outer level of a two-level panel", and a
// reader moving between the two tabs should not have to re-learn what a
// section header looks like.
export const AttachmentsPanelSectionButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${themeGet('space.xs')}px;
  width: 100%;
  padding: ${themeGet('space.xs')}px 0;
  margin: 0;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  color: ${palette.neutral[900]};
`

export const AttachmentsPanelSectionName = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

// Sections after the first get space above them, which the collapsed state
// needs: without it a run of closed sections reads as one dense list.
export const AttachmentsPanelSection = styled.div`
  & + & {
    margin-top: ${themeGet('space.m')}px;
  }
`

// Inset from the section header, the way the comments rail insets a thread
// inside its section. Padding on BOTH sides, not just the left: the chevron is
// right-aligned, so an inset that only moved the text would leave the two
// levels' icons stacked in one column and the nesting invisible.
export const AttachmentsPanelQuestionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  padding: 0 ${themeGet('space.xs')}px;
`

export const AttachmentsPanelQuestionButton = styled.button`
  text-align: left;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

// Identical to QuestionnaireRailThreadTitle: "Question # 4" means the same
// thing in both panels and is read in the same glance, so it is set the same
// way. It was a step smaller here, which made the two tabs look like they came
// from different screens.
export const AttachmentsPanelQuestionNumber = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${palette.neutral[900]};
`

export const AttachmentsPanelQuestionText = styled.span`
  font-size: 13px;
  color: ${palette.neutral[600]};
`

// A real button, not a clickable div: opening a preview is an action, and a
// div with only an onClick cannot be reached or fired from the keyboard.
export const AttachmentsPanelCard = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  width: 100%;
  text-align: left;
  background: none;
  border: 1px solid ${palette.neutral[200]};
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background: ${palette.neutral[30]};
  }
`

export const AttachmentsPanelCardBody = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

export const AttachmentsPanelCardName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${palette.neutral[900]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const AttachmentsPanelCardDate = styled.span`
  font-size: 12px;
  color: ${palette.neutral[400]};
`

// The card the Attachments index sits in, matching the Comments index's
// boundary so the two tabs read as the same surface.
export const AttachmentsPanelCardOuter = styled(QuestionnaireSidebarPanel)`
  overflow: hidden;
  min-height: 0;
`

// The inner row, boxed like the comments rail's thread row: same vertical
// padding, same gap. The rail's is a single button; this one holds two controls
// side by side, so the box lives on the wrapper rather than on a button.
export const AttachmentsPanelQuestionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${themeGet('space.xs')}px;
  width: 100%;
  padding: ${themeGet('space.xs')}px 0;
`

// Just the hit area. The glyph inside is ShowPageAccordionIcon, the same one
// the section header and the comments rail use, which owns its own open/closed
// rotation -- so there is no transform here to keep in step with it.
export const AttachmentsPanelQuestionChevron = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`

// Pre-submit "what is still outstanding" list. Scrolls rather than growing the
// dialog: a long questionnaire can leave many required fields unfilled.
export const IncompleteList = styled.ul`
  list-style: none;
  margin: 16px 0 0;
  padding: 0;
  max-height: 320px;
  overflow-y: auto;
`

export const IncompleteListItem = styled.li`
  padding: 8px 0;
  border-top: 1px solid ${palette.neutral[100]};
`

export const IncompleteSectionLabel = styled.div`
  font-size: 12px;
  color: ${palette.neutral[500]};
`

export const IncompleteQuestionText = styled.div`
  font-weight: 500;
`

export const IncompleteMissing = styled.div`
  font-size: 13px;
  color: ${palette.orange[800]};
`

// Show-page pager ("1 of 7" with prev/next), shared by every questionnaire show
// page. These lived once per surface until the copies drifted -- only one had
// the wrapping rules below, so elsewhere "1 of 7" broke across two lines.
export const PagerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  // "1 of 7" is one phrase: without these it wraps between "of" and the count
  // when the header runs out of room.
  white-space: nowrap;
  flex-shrink: 0;
  color: ${palette.neutral[500]};
  font-size: 14px;
`

export const PagerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${palette.neutral[500]};

  &:disabled {
    color: ${palette.neutral[200]};
    cursor: default;
  }

  svg {
    width: 10px;
    height: 10px;
  }
`

// An answer on a read-only surface: the text itself, with the whitespace and
// wrapping a saved multi-line answer needs.
export const ReadOnlyAnswerText = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: ${palette.neutral[900]};
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  padding: 4px 0;
`

// The three-column answering layout: left index, the document, the rail.
//
// Lifted out of the three pages that each wrote it separately (vendor portal
// answer, main app answer, customer read-only) so the rail lands in one place
// instead of three.
export const QuestionnaireWorkspaceBody = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${themeGet('space.l')}px;
  /* Fills what is left of the column rather than measuring the window. It used
     to be a calc against 100vh, which took the whole height for itself and
     ignored the action row and its margin sitting above it in the same column
     -- so the column always overflowed by about that much, whatever the
     content, and the section nav at the bottom could only be reached by
     scrolling. */
  flex: 1 1 auto;
  min-height: 500px;
  /* Lets the row itself be squeezed -- without this it keeps its content width
     and its columns spill out over one another rather than compressing, which
     is what the copilot panel opening beside it used to produce. */
  min-width: 0;
`

export const QuestionnaireWorkspaceSidebar = styled.div`
  /* Shrinkable, not fixed: at a fixed basis this column refused to give up any
     width, so a narrow row overflowed instead of compressing and the columns
     drew on top of each other. It still asks for its width and stops at 310,
     below which the section names are not worth reading.
     430 rather than 380 since the panel took on Overview and Versions: the
     metadata stacks down it now, and the strip carries four tabs. */
  flex: 0 1 430px;
  min-width: 310px;
  display: flex;
  flex-direction: column;
  height: 100%;
  /* No bottom padding: the document column beside it has none either, and this
     panel's bodies are bordered cards -- 24px here left every one of those
     borders stopping short of the section nav across the way. */
`

// The document side of the row: header, then the questions-and-rail band, then
// the section stepper. A column so the header and stepper span the full width
// while the two panels share the space between them.
export const QuestionnaireWorkspaceMain = styled.div`
  flex: 1 1 0%;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
`

// The band the questions and the comments rail share. Both are children here,
// so they begin and end at the same height by construction rather than by one
// of them being padded to match the other.
// No gap here, deliberately. The rail collapses to zero width rather than
// unmounting, and a flex gap is drawn between items whatever their size -- so a
// gap left the answering view with a 24px gutter against nothing, on every page
// where the rail was closed. The spacing belongs to the rail instead, where it
// can collapse with it.
export const QuestionnaireWorkspacePanels = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  display: flex;
  align-items: stretch;
`

// A full-height column, so the bordered panel inside it reaches the bottom of
// the band rather than stopping under the last question.
export const QuestionnaireWorkspaceDocument = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
`

// The rail animates its WIDTH rather than sliding over the document
// (ArtifactSidePanel's translateX keyframe is for an overlay panel): this is a
// real column, so the document should widen as the rail closes rather than be
// covered by it.
//
// Stays mounted while closed so there is something to animate, and so a thread
// the URL names is already rendered when the rail opens.
export const QuestionnaireRailColumn = styled.div<{ $isOpen: boolean }>`
  flex: 0 0 ${({ $isOpen }) => ($isOpen ? '360px' : '0px')};
  width: ${({ $isOpen }) => ($isOpen ? '360px' : '0px')};
  min-width: 0;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  /* The gutter between the document and the rail, carried by the rail so it
     disappears when the rail does -- see QuestionnaireWorkspacePanels. Animated
     with the width, or the document would jump the full 24px at the start of a
     200ms collapse. */
  margin-left: 0;
  ${({ $isOpen }) =>
    $isOpen &&
    css`
      margin-left: ${themeGet('space.l')}px;
    `}
  transition: flex-basis 200ms ease-in-out, width 200ms ease-in-out,
    margin-left 200ms ease-in-out;

  /* A width transition is decoration, not information -- honour the setting. */
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

// Fixed to the open width so the rail's contents do not reflow while the
// column animates -- text reflowing mid-transition reads as a stutter.
export const QuestionnaireRailInner = styled.div`
  width: 360px;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: ${palette.neutral.white};
  border: 1px solid ${palette.neutral[100]};
  border-radius: 12px;
`

export const QuestionnaireRailHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${themeGet('space.xs')}px;
  padding: ${themeGet('space.xs')}px ${themeGet('space.m')}px;
  border-bottom: 1px solid ${palette.neutral[100]};
  flex: 0 0 auto;
`

export const QuestionnaireRailTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${palette.neutral[900]};
`

// An icon-only header action. Borderless: these sit beside a solid primary
// button, and outlining every icon turns a row of quiet actions into a row of
// competing buttons. The hover and active backgrounds are what make it read as
// pressable.
export const QuestionnaireIconButton = styled.button<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  /* 28px around a 20px icon: 4px of padding a side. With the row's gap between
     two icon buttons tightened to 4px (see QuestionnaireHeaderActions) that
     puts neighbouring ICONS 4 + 4 + 4 = 12px apart. Box-to-box spacing is not
     what the eye measures here -- the glyphs are -- and a hover background
     that hugs the icon reads as part of it rather than as a button. */
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  background: ${({ $isActive }) =>
    $isActive ? palette.neutral[50] : 'transparent'};

  &:hover {
    background: ${palette.neutral[50]};
  }
`

export const QuestionnaireHeaderIconWrap = styled.span`
  position: relative;
  display: inline-flex;
  width: 20px;
  height: 20px;
`

// Sizes and colours whatever icon it is handed, so every header action matches
// without each caller styling its own svg.
export const QuestionnaireHeaderIcon = styled.span`
  display: inline-flex;

  svg {
    width: 20px;
    height: 20px;
  }

  svg path {
    fill: ${palette.neutral[700]};
  }
`

// The same 8px Magenta/600 dot the question rows use, so "unread" looks
// identical wherever it appears.
export const QuestionnaireHeaderIconDot = styled.span`
  position: absolute;
  top: -2px;
  right: -3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${palette.magenta[600]};
`

// The header itself: breadcrumb on the left, actions on the right. Shared by
// all three questionnaire surfaces, which each had a byte-identical copy.
//
// Wraps as a whole: with the copilot open the header has a fraction of its
// usual width, and the actions dropping BELOW the breadcrumb is far better than
// the breadcrumb truncating to "Que > Sec..." beside them. The actions block
// refuses to shrink so it moves as a unit rather than its buttons folding
// individually -- which was an earlier attempt, and looked worse than the
// problem it solved.
// $actionsOnly is set where the breadcrumb beside the actions is not rendered
// -- the answering view embedded in a tab, whose page already draws the title
// and back link. With one child left, space-between would park the actions on
// the left; they belong hard against the right edge either way.
export const QuestionnaireHeaderContainer = styled.div<{
  $actionsOnly?: boolean
}>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: ${({ $actionsOnly }) =>
    $actionsOnly ? 'flex-end' : 'space-between'};
  align-items: center;
  gap: ${themeGet('space.s')}px;
  margin-bottom: ${themeGet('space.l')}px;
`

// The header's action row, shared by all three questionnaire surfaces -- they
// each had a byte-identical copy of it.
//
// It normalises the height of what it holds, because the buttons disagree:
// StandardButton's medium is 40px, and DropdownPrimaryButton hard-codes 32px
// with no size prop, so "More Actions" sat visibly short beside the others.
// Normalising here rather than changing DropdownPrimaryButton, which is used
// across the app and would shift button heights on pages nobody has looked at.
export const QuestionnaireHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
  /* Never squeezed: the row wraps below the breadcrumb instead (see
     QuestionnaireHeaderContainer), so these keep their size. */
  flex-shrink: 0;
  /* Stays on the right when the row wraps it onto a line of its own. */
  margin-left: auto;

  /* The dropdown renders its toggle inside a wrapper div, so both shapes are
     matched: a button in the row, and a button one level down. Icon buttons
     size themselves and are excluded. */
  > button:not(${/* sc-selector */ QuestionnaireIconButton}),
  > div > button {
    height: 40px;
  }

  /* Icon buttons form a cluster: 4px between their boxes, which with their 4px
     of internal padding puts the icons themselves 12px apart. The full 12px
     still separates the cluster from a labelled button beside it. */
  ${QuestionnaireIconButton} + ${QuestionnaireIconButton} {
    margin-left: -8px;
  }
`

// Section stepper under the question list, so finishing a section leads
// straight into the next one instead of back to the sidebar.
export const SectionNavRow = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: ${themeGet('space.m')}px;
  margin-top: ${themeGet('space.l')}px;
  /* No padding below: this row is the bottom of the document column, and the
     comments rail beside it ends where this row's box ends. Padding here left
     the buttons stopping 24px short of the rail's border, which read as the
     comments panel being taller than the answers. */
`

// Both ends keep their slot when there's nowhere to go, so the row doesn't
// reflow as the vendor steps through sections.
export const SectionNavButton = styled.button<{ $align: 'start' | 'end' }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $align }) =>
    $align === 'start' ? 'flex-start' : 'flex-end'};
  gap: 2px;
  flex: 0 1 45%;
  min-width: 0;
  padding: ${themeGet('space.xs')}px ${themeGet('space.m')}px;
  background: ${palette.neutral.white};
  border: 1px solid ${palette.neutral[100]};
  border-radius: 8px;
  cursor: pointer;
  text-align: ${({ $align }) => ($align === 'start' ? 'left' : 'right')};

  &:hover:not(:disabled) {
    border-color: ${palette.blue[300]};
    background: ${palette.blue[50]};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`

export const SectionNavDirection = styled.span`
  font-size: 12px;
  color: ${palette.neutral[500]};
`

// The section name can be arbitrarily long, so it truncates rather than
// stretching the row.
export const SectionNavName = styled.span`
  max-width: 100%;
  font-size: 14px;
  font-weight: 600;
  color: ${palette.neutral[900]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

// The questionnaire's name reads as the last crumb of the breadcrumb rather
// than as a page title of its own: same size as the crumb before it, darker and
// heavier, and not a link, because there is nowhere for it to go. Shared by the
// three show pages and by the answering view's own breadcrumb, which had two
// byte-identical copies of these between them.
export const QuestionnaireBreadcrumbSeparator = styled.span`
  color: ${palette.neutral[400]};
  /* The show pages put this straight into ShowPageRouteTitle, which sets no
     gap of its own, so the slash carries its own breathing room. */
  margin: 0 ${themeGet('space.xs')}px;
`

export const QuestionnaireBreadcrumbTitle = styled.span`
  color: ${palette.neutral[900]};
  font-weight: 600;
`

// The answering view's root. It lives inside a tab on every surface now, and
// the tab supplies the page inset, so this supplies none -- it used to render
// ShowPageContainer, whose top and side padding landed a second page inset
// within the first.
export const AnswerViewContainer = styled.div`
  position: relative;
  height: 100%;
  /* A column, so the workspace below the action row can take exactly the space
     the action row leaves -- and growing, so there is space to divide when the
     tab hands its height down. */
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
`

// The questionnaire show pages' body. ShowPageItemBody carries a 40px bottom
// margin, which on a page whose tab fills the window leaves a band of dead
// space under both panels -- the answering view already ends where the window
// does.
// ShowPageContainer is a block, so a flex child of it cannot grow -- the page
// sized to its content and left the rest of the window empty. A column here
// starts the chain that carries the window's height down to the answering view.
export const QuestionnaireShowContainer = styled(ShowPageContainer)`
  display: flex;
  flex-direction: column;
  min-height: 0;
`

// The grid ShowPageItem draws does not carry a height, and the answering view
// below needs one to fill. It has a single child here, so a column costs
// nothing and passes the height straight through.
export const QuestionnaireShowItem = styled(ShowPageItem)`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
`

export const QuestionnaireShowItemBody = styled(ShowPageItemBody)`
  margin-bottom: 0;
  /* Passes the page's height down to the tabs, which pass it to the answering
     view. Without a definite height the whole way, the workspace has nothing
     to fill and collapses to its content. */
  min-height: 0;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
`

// The Overview tab's body. It lives in the left panel now rather than across
// the width of a page of its own, so the two detail columns become one stack --
// at 380px, and 260px when the panel is squeezed, side by side is not a layout.
export const QuestionnaireOverviewPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${themeGet('space.xs')}px;
  min-width: 0;
  /* Fills the panel so its border ends level with the section nav across the
     way, rather than stopping wherever the last field happens to. */
  flex: 1 1 auto;
  overflow-y: auto;
  /* The card every other body in this panel is drawn as, so Overview does not
     read as loose text where sections and attachments read as objects. */
  padding: 14px 16px;
  border: 1px solid ${palette.neutral[100]};
  border-radius: 12px;
  background: ${palette.neutral.white};
`

// One submitted version. A card rather than a table row: the panel is 430px
// wide, where four columns and a menu could only be read by scrolling
// sideways.
export const VersionCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${themeGet('space.xs')}px;
  padding: 14px 16px;
  border: 1px solid ${palette.neutral[100]};
  border-radius: 12px;
  background: ${palette.neutral.white};
`

export const VersionCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${themeGet('space.xs')}px;
`

export const VersionCardDate = styled.div`
  font-weight: 600;
  color: ${palette.neutral[900]};
`

// The facts under the date, as "label: value" lines rather than columns.
export const VersionCardMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: ${palette.neutral[600]};
  font-size: 13px;
`

export const VersionCardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${themeGet('space.xs')}px;
  /* Level with the section nav, like the other bodies in this panel. */
  flex: 1 1 auto;
  overflow-y: auto;
`

// Sits where the rest of the panel's content sits, rather than in a grid of
// its own that ignored the column it was in.
// The card only. What goes inside is the same centred block the Attachments
// panel uses (QuestionnaireEmptyStateContainer + Title), so the two empty
// states in this show page sit and read the same way -- this one used to draw
// its own text flush to the top in a different size and colour.
export const VersionsEmptyState = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  border: 1px solid ${palette.neutral[100]};
  border-radius: 12px;
  background: ${palette.neutral.white};
`

// The "Why this answer" panel: the rationale and the sources it rests on, set
// back from the answer on its own ground so it reads as commentary rather than
// as part of what the responder said.
// The rounded block holds the WHOLE accordion -- the "Why this answer" toggle
// and what it opens -- not just the expanded content.
//
// neutral[30] and 16px are the design's values. The border is not: the design
// draws question cards on white, but the ACTIVE question here is itself
// neutral[30] (.is-active on the question accordion item), so on the card a
// reader is actually looking at, fill alone left the panel invisible and the
// tint read as covering the whole question. A hairline is the smallest thing
// that keeps the block legible on both grounds without departing from the
// specified fill.
export const AnswerRationalePanel = styled.div`
  padding: 12px 16px;
  margin-top: 16px;
  border-radius: 16px;
  background: ${palette.neutral[30]};
  border: 1px solid ${palette.neutral[50]};

  // TextAccordion separates its header from its body by 8px, which left the
  // label floating above its own content. The label belongs to what it opens.
  > div {
    gap: 0;
  }
`

// What the toggle opens. The panel above owns the block; this owns the rhythm
// between the rationale, the shortfall and the sources.
export const AnswerRationaleBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 4px;
`

export const AnswerRationaleLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${palette.neutral[700]};
`

export const AnswerRationaleText = styled.div`
  font-size: 14px;
  line-height: 1.35;
  color: ${palette.neutral[900]};
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`
